"use client";

import React from 'react';
import { Sparkles, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Image from 'next/image';
import Link from 'next/link';
import { useLocale } from 'next-intl';

export default function AiCareerPage() {
  const locale = useLocale();
  const isEn = locale === 'en';

  const images = [
    '/ai-kariyer-1.png',
    '/ai-kariyer-2.png',
    '/ai-kariyer-3.png',
    '/ai-kariyer-4.png',
    '/ai-kariyer-5.png',
    '/ai-kariyer-6.png'
  ];

  return (
    <div className="w-full max-w-full overflow-x-hidden bg-slate-50 dark:bg-slate-950 py-4 sm:py-8 px-4 sm:px-6 flex-1 flex flex-col">
      <div className="max-w-5xl mx-auto w-full bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
        
        {/* Banner header inside page */}
        <div className="w-full bg-brand text-white py-4 px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
          <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-300 animate-pulse shrink-0" />
          <h1 className="text-base sm:text-2xl font-bold tracking-wide text-center">
            {isEn ? 'NEW: AI-Powered Career Page is Live!' : 'YENİ: AI Destekli Kariyer Ekranı Yayında!'}
          </h1>
        </div>

        <div className="p-4 sm:p-8 md:p-12 space-y-8 min-w-0 break-words">
          <div className="text-center space-y-6 max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white leading-tight">
              {isEn ? 'AI-Powered Career Page Creation Feature is Now Live!' : 'AI Destekli Kariyer Sayfası Oluşturma Özelliğimiz Yayında!'}
            </h2>
          </div>

          <div className="space-y-6 text-lg sm:text-xl text-slate-700 dark:text-slate-300 leading-relaxed font-medium max-w-4xl mx-auto">
            <p>
              {isEn 
                ? 'Showcasing your career and achievements in the digital world is now much easier. Add your CV, a free text, or a list of your career history to our AI assistant; leave the rest to it!'
                : 'Kariyerinizi ve başarılarınızı dijital dünyada en etkileyici haliyle sergilemek artık çok daha kolay. Yapay zeka asistanımıza CV\'nizi, serbest bir metni veya liste halindeki kariyer geçmişinizi ekleyin; gerisini ona bırakın!'}
            </p>
            
            <p>
              {isEn 
                ? 'Our smart assistant analyzes your information in seconds, finds and organizes the most suitable visuals for your content, and showcases your career in a stunning way with 3 different aesthetic design options. Plus, you are in full control; you can edit, add, or remove generated content as you wish.'
                : 'Akıllı asistanımız saniyeler içinde bilgilerinizi analiz eder, içeriğinize en uygun görselleri bulup düzenler ve kariyerinizi 3 farklı estetik tasarım seçeneği ile göz alıcı bir şekilde sergiler. Üstelik kontrol tamamen sizde; oluşturulan içerikler üzerinde dilediğiniz gibi düzenleme, ekleme veya çıkarma işlemlerini kendiniz de yapabilirsiniz.'}
            </p>
            
            <div className="bg-brand/5 p-6 rounded-2xl border border-brand/20 text-xl font-bold text-brand dark:text-brand-light text-center shadow-sm">
              {isEn 
                ? 'Take your place on Biyografya, the only platform that reflects you from every angle. The premium package (extra features) comes free for the first year with your biography page.'
                : 'Sizi her yönünüzle yansıtan tek platform: Biyografya.com\'daki yerinizi alın. Biyografi sayfanızla birlikte premium paket (ek özellikler) ilk yıl hediye.'}
            </div>
          </div>

          {/* Screenshots Area */}
          <div className="pt-8">
            <h3 className="text-2xl font-bold text-center text-slate-900 dark:text-white mb-8">
              {isEn ? 'Here is how it looks' : 'İşte böyle görünüyor'}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {images.map((img, idx) => (
                <div 
                  key={idx}
                  className="relative rounded-2xl overflow-hidden border-2 border-slate-200 dark:border-slate-800 shadow-md bg-slate-100 dark:bg-slate-900 flex items-center justify-center hover:scale-[1.02] transition-transform duration-300"
                >
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 p-4 text-center z-0">
                    <span className="font-semibold text-sm">{isEn ? `Image ${idx + 1}` : `Görsel ${idx + 1}`}</span>
                  </div>
                  <Image 
                    src={img} 
                    alt={isEn ? `AI Career Page ${idx + 1}` : `AI Kariyer Ekranı ${idx + 1}`} 
                    width={800}
                    height={1200}
                    className="w-full h-auto z-10 opacity-0 transition-opacity duration-300" 
                    onLoad={(e) => e.currentTarget.classList.remove('opacity-0')}
                    onError={(e) => e.currentTarget.classList.add('hidden')}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-center pt-8 pb-4 border-t border-slate-200 dark:border-slate-800 mt-12">
            <Button asChild size="lg" className="w-full sm:w-auto px-12 py-8 text-xl rounded-full shadow-xl bg-brand hover:bg-brand-light hover:scale-105 transition-all">
              <Link href={`/${locale}/publish`}>
                {isEn ? 'Start Creating Your Profile' : 'Sayfanızı Oluşturun'}
              </Link>
            </Button>
          </div>

        </div>
      </div>
    </div>
  );
}
